import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AddClientDetails, MediaResource } from '../types/addClientType';

// File objects (profileFile, contractFiles) cannot be serialised to sessionStorage.
// We persist the text fields plus the MediaResource metadata for already-uploaded
// files (filename, fileSize, path, resourceId — all serialisable).
// Deletion intent flags track user intent to delete resources and survive page refreshes.
export type PersistedClientForm = Omit<
  AddClientDetails,
  'profileFile' | 'contractFiles'
> & {
  contractDocs: MediaResource[];
  profileResource?: MediaResource | null;
  profileBase64?: string;
  subscriptionPlanLabel?: string;
  // Deletion intent flags - persisted to survive page refresh
  profileDeletionIntent?: boolean;
  docsDeletionIntent?: Record<string, boolean>; // keyed by resourceId
};

export type AddClientStoreState = {
  // Keyed by clientId so multiple drafts don't collide.
  drafts: Record<string, PersistedClientForm>;
  saveDraft: (clientId: string, form: PersistedClientForm) => void;
  getDraft: (clientId: string) => PersistedClientForm | undefined;
  clearDraft: (clientId: string) => void;

  /**
   * Whether a pending (unsaved) equipment form is open.
   * Keyed by facilityId (tempFacilityId / serverFacilityId).
   */
  pendingForms: Record<string, boolean>;
  setHasPendingForm: (facilityId: string, value: boolean) => void;
  getHasPendingForm: (facilityId: string) => boolean;

  /**
   * Server IDs for each equipment slot, keyed by facilityId.
   * undefined means the slot has not been submitted yet.
   */
  equipmentServerIds: Record<string, (string | undefined)[]>;
  setEquipmentServerIds: (
    facilityId: string,
    ids: (string | undefined)[]
  ) => void;
  getEquipmentServerIds: (facilityId: string) => (string | undefined)[];
};

export const useAddClientStore = create<AddClientStoreState>()(
  persist(
    (set, get) => ({
      drafts: {},

      saveDraft: (clientId, form) =>
        set((state) => ({
          drafts: { ...state.drafts, [clientId]: form },
        })),

      getDraft: (clientId) => get().drafts[clientId],

      clearDraft: (clientId) =>
        set((state) => {
          const { [clientId]: _, ...rest } = state.drafts;
          return { drafts: rest };
        }),

      pendingForms: {},

      setHasPendingForm: (facilityId, value) =>
        set((state) => ({
          pendingForms: { ...state.pendingForms, [facilityId]: value },
        })),

      getHasPendingForm: (facilityId) =>
        get().pendingForms[facilityId] ?? false,

      equipmentServerIds: {},

      setEquipmentServerIds: (facilityId, ids) =>
        set((state) => ({
          equipmentServerIds: {
            ...state.equipmentServerIds,
            [facilityId]: ids,
          },
        })),

      getEquipmentServerIds: (facilityId) =>
        get().equipmentServerIds[facilityId] ?? [],
    }),
    {
      name: 'add-client-drafts',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);
